import { Component, ChangeDetectionStrategy, Input, OnChanges, SimpleChanges, inject } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { BehaviorSubject, takeUntil } from 'rxjs';
import { TuiDay } from '@taiga-ui/cdk';
import { TranslateService } from '@ngx-translate/core';
import { AlertService, DocumentService } from '@src/core/services';
import { TuiDayTimeTransformer } from '@src/utils';
import { EnvService } from '@src/app/modules/env';
import { Optional } from '@src/types/utils';
import { BaseResizableComponent } from '@src/app/modules/base';

import { GetReportForUnionResultDtoUI } from '../types';
import { ReportsService } from '../services';

import { FormData, FormDataControls } from './types';
import { DATE_FIELD_NAMES } from './constants';

@Component({
  selector: 'report-form',
  templateUrl: './report-form.component.html',
  styleUrls: ['./report-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReportFormComponent extends BaseResizableComponent implements OnChanges {
  private readonly alertService = inject(AlertService);
  private readonly translateService = inject(TranslateService);
  private readonly reportsService = inject(ReportsService);
  private readonly env = inject(EnvService);
  private readonly documentService = inject(DocumentService);

  @Input() data?: GetReportForUnionResultDtoUI;
  fieldNames?: string[];
  form: FormGroup<FormDataControls>;
  isLoading$ = new BehaviorSubject<boolean>(false);

  constructor() {
    super();

    this.form = new FormGroup<FormDataControls>({
      id: new FormControl<Optional<string>>(undefined),
      dateFrom: new FormControl<Optional<TuiDay>>(undefined),
      dateTo: new FormControl<Optional<TuiDay>>(undefined),
    });
  }

  override ngOnDestroy(): void {
    this.isLoading$.next(false);
    this.isLoading$.complete();

    super.ngOnDestroy();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data']) {
      if (this.data) {
        this.mapDataToFormData(this.data);
        this.fieldNames = this.getFieldNames(this.data.reportData);
      }
    }

    this.cdr.markForCheck();
  }

  getFieldNames(reportData?: Object) {
    return reportData ? Object.keys(reportData) : undefined;
  }

  onSubmit(): void {
    this.form.markAllAsTouched();

    let errorMessage = '';
    if (this.form.invalid) {
      errorMessage = this.translateService.instant('common.alerts.errors.fillRequired');
    }

    if (errorMessage) {
      this.alertService.error(errorMessage);
      return;
    }

    this.setLoading(true);

    const { id } = this.form.value as FormData;

    const reportData = this.getReportData(this.form.value, this.fieldNames);

    this.reportsService
      .buildReport(id, {
        reportData: {
          ...reportData,
          ...this.data?.hiddenReportData,
        },
      })
      .pipe(takeUntil(this.destroyed$$))
      .subscribe(resultId => {
        this.getReportResult(resultId);
      });
  }

  private getReportResult(resultId: string) {
    this.reportsService
      .getReportResult(resultId, this.env.isBot)
      .pipe(takeUntil(this.destroyed$$))
      .subscribe({
        next: response => {
          if (response.body) {
            this.env.downloadFileByHttpResponse(response);
            this.setLoading(false);
          } else {
            setTimeout(() => {
              this.getReportResult(resultId);
            }, 2000);
          }
        },
        error: () => {
          this.setLoading(false);
        },
      });
  }

  private mapDataToFormData(data: GetReportForUnionResultDtoUI) {
    this.form.patchValue({ id: data.id });
  }

  private setLoading(value: boolean) {
    this.isLoading$.next(value);
    value ? this.form.disable() : this.form.enable();
  }

  private getReportData(formData: Record<string, any>, fieldNames: string[] = []) {
    const reportData: Record<string, any> = {};
    fieldNames.forEach(fieldName => {
      let value: any = formData[fieldName];
      if (DATE_FIELD_NAMES.includes(fieldName)) {
        value = TuiDayTimeTransformer.tuiDayToDate(value);
      }
      reportData[fieldName] = value;
    });

    return reportData;
  }
}
